import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addMonths, subMonths, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Clock, User, Phone, FileText, X, RefreshCw } from 'lucide-react';

import { DurationSelect, SLOT_MINUTES, DEFAULT_DURATION } from '../components/DurationSelect';
import { AvailableTimeSelect } from '../components/AvailableTimeSelect';
const locales = { 'es': es };
const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) => format(date, formatStr, { ...options, locale: es }),
  parse: (str, formatStr, baseDate, options) => parse(str, formatStr, baseDate, { ...options, locale: es }),
  startOfWeek: (date) => startOfWeek(date, { locale: es }),
  getDay,
  locales,
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarPage = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('week');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    patient_name: '',
    patient_phone: '',
    appointment_date: '',
    appointment_time: '',
    duration_minutes: DEFAULT_DURATION,
    reason_text: '',
    internal_notes: ''
  });
  const [unavailableDates, setUnavailableDates] = useState([]);

  // Fetch unavailable dates for current month view (optimized - single API call)
  const fetchUnavailableDates = useCallback(async (monthDate) => {
    try {
      const startDate = startOfMonth(monthDate);
      const endDate = endOfMonth(addMonths(monthDate, 1));
      
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      
      // Single API call to get all unavailable dates
      const response = await axios.get(`${API}/availability/unavailable-dates`, {
        params: { start_date: startDateStr, end_date: endDateStr }
      });
      
      setUnavailableDates(response.data.unavailable_dates || []);
    } catch (err) {
      console.error('Error fetching unavailable dates:', err);
    }
  }, []);

  const checkGoogleConnection = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleConnected(response.data.connected);
    } catch (error) {
      console.error('Error checking Google Calendar status:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchUnavailableDates(currentDate);
  }, [currentDate, fetchUnavailableDates]);

  useEffect(() => {
    checkGoogleConnection();
  }, [checkGoogleConnection]);

  const syncAllToGoogle = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/calendar/google/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`${response.data.message}: ${response.data.synced} citas sincronizadas`);
      fetchAppointments();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const fetchAppointments = useCallback(async () => {
    try {
      const start = format(startOfMonth(subMonths(currentDate, 1)), 'yyyy-MM-dd');
      const end = format(endOfMonth(addMonths(currentDate, 1)), 'yyyy-MM-dd');
      
      const response = await axios.get(`${API}/calendar/range?start_date=${start}&end_date=${end}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const rawEvents = response.data
        .filter(apt => apt.appointment_date && apt.appointment_time)
        .map(apt => {
          const startDate = new Date(`${apt.appointment_date}T${apt.appointment_time}:00`);
          const endDate = new Date(startDate.getTime() + (apt.duration_minutes || DEFAULT_DURATION) * 60000);
          return {
            id: apt.id,
            title: apt.patient_name || 'Sin nombre',
            start: startDate,
            end: endDate,
            resource: apt,
            isGoogleEvent: apt.source === 'google' || apt.status === 'google_event'
          };
        })
        .filter(e => !isNaN(e.start?.getTime()));
      
      // Filter events that are within business hours (7:00 - 17:00)
      const calendarEvents = rawEvents.filter(e => {
        const hour = e.start.getHours();
        return hour >= 7 && hour < 17;
      });
      
      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentDate]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleSelectSlot = ({ start }) => {
    // When clicking on an empty slot, open new appointment dialog with pre-filled date/time
    const dateStr = format(start, 'yyyy-MM-dd');
    const timeStr = format(start, 'HH:mm');
    setNewAppointment({
      ...newAppointment,
      appointment_date: dateStr,
      appointment_time: timeStr
    });
    setShowNewAppointment(true);
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setNewAppointment({ ...newAppointment, appointment_date: date, appointment_time: '' });
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/appointments`, {
        ...newAppointment,
        source: 'manual'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowNewAppointment(false);
      setNewAppointment({
        patient_name: '',
        patient_phone: '',
        appointment_date: '',
        appointment_time: '',
        duration_minutes: DEFAULT_DURATION,
        reason_text: '',
        internal_notes: ''
      });
      fetchAppointments();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear cita');
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAppointments();
      setSelectedEvent(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar cita');
    }
  };

  const eventStyleGetter = (event) => {
    const status = event.resource?.status || 'pending';
    const source = event.resource?.source || 'manual';
    
    // Special style for Google Calendar events
    if (source === 'google' || status === 'google_event') {
      return {
        style: {
          backgroundColor: '#8B5CF6',
          borderColor: '#7C3AED',
          borderRadius: '6px',
          border: '2px solid #7C3AED',
          color: 'white',
          fontWeight: 500,
          opacity: 0.85
        }
      };
    }
    
    const colors = {
      agendada: { backgroundColor: '#F59E0B', borderColor: '#D97706' },
      confirmada: { backgroundColor: '#10B981', borderColor: '#059669' },
      en_consulta: { backgroundColor: '#8B5CF6', borderColor: '#7C3AED' },
      atendida: { backgroundColor: '#3B82F6', borderColor: '#2563EB' },
      cancelada: { backgroundColor: '#EF4444', borderColor: '#DC2626' },
      no_asistio: { backgroundColor: '#6B7280', borderColor: '#4B5563' },
      // Legacy english values for backwards compatibility
      pending: { backgroundColor: '#F59E0B', borderColor: '#D97706' },
      confirmed: { backgroundColor: '#10B981', borderColor: '#059669' },
      attended: { backgroundColor: '#3B82F6', borderColor: '#2563EB' },
      cancelled: { backgroundColor: '#EF4444', borderColor: '#DC2626' },
      no_show: { backgroundColor: '#6B7280', borderColor: '#4B5563' }
    };
    return {
      style: {
        ...colors[status],
        borderRadius: '6px',
        border: `2px solid ${colors[status]?.borderColor || '#6B7280'}`,
        color: 'white',
        fontWeight: 500
      }
    };
  };

  const getStatusLabel = (status) => {
    const labels = {
      agendada: 'Agendada',
      confirmada: 'Confirmada',
      en_consulta: 'En consulta',
      atendida: 'Atendida',
      cancelada: 'Cancelada',
      no_asistio: 'No asistió',
      // Legacy english values
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      attended: 'Atendida',
      cancelled: 'Cancelada',
      no_show: 'No asistió',
      google_event: 'Google Calendar'
    };
    return labels[status] || status;
  };

  const messages = {
    today: 'Hoy',
    previous: 'Anterior',
    next: 'Siguiente',
    month: 'Mes',
    week: 'Semana',
    day: 'Día',
    agenda: 'Agenda',
    date: 'Fecha',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'No hay citas en este rango'
  };

  // Style unavailable days in calendar
  const dayPropGetter = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    if (unavailableDates.includes(dateStr)) {
      return {
        className: 'rbc-unavailable-day',
        style: {
          backgroundColor: '#FECACA',
          position: 'relative'
        }
      };
    }
    return {};
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">Calendario</h1>
          <p className="text-slate-500 mt-1">Gestiona las citas del consultorio</p>
        </div>
        <div className="flex gap-2">
          {googleConnected && (
            <Button 
              onClick={syncAllToGoogle}
              variant="outline"
              disabled={syncing}
              data-testid="sync-google-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sincronizando...' : 'Sincronizar Google'}
            </Button>
          )}
          <Button 
            onClick={() => setShowNewAppointment(true)}
            className="bg-[#0891B2] hover:bg-[#0e7490]"
            data-testid="new-appointment-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Cita
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        <Badge className="status-pending">Pendiente</Badge>
        <Badge className="status-confirmed">Confirmada</Badge>
        <Badge className="status-attended">Atendida</Badge>
        <Badge className="status-cancelled">Cancelada</Badge>
        <Badge className="status-no_show">No asistió</Badge>
        {googleConnected && (
          <Badge className="bg-purple-100 text-purple-700">Google Calendar</Badge>
        )}
        <Badge className="bg-red-100 text-red-700 line-through">Sin disponibilidad</Badge>
      </div>

      {/* Calendar */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <Calendar
            localizer={localizer}
            culture="es"
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 700 }}
            eventPropGetter={eventStyleGetter}
            dayPropGetter={dayPropGetter}
            onSelectEvent={(event) => {
              // Don't open details for Google events, just show info
              if (event.resource?.source === 'google' || event.resource?.status === 'google_event') {
                alert(`Evento de Google Calendar:\n${event.resource?.patient_name}`);
              } else {
                setSelectedEvent(event.resource);
              }
            }}
            onSelectSlot={handleSelectSlot}
            date={currentDate}
            view={currentView}
            onNavigate={(date) => setCurrentDate(date)}
            onView={(view) => setCurrentView(view)}
            selectable
            step={SLOT_MINUTES}
            timeslots={6}
            messages={messages}
            views={['month', 'week', 'day']}
            min={new Date(2026, 0, 1, 7, 0, 0)}
            max={new Date(2026, 0, 1, 17, 0, 0)}
            scrollToTime={new Date(2026, 0, 1, 7, 0, 0)}
            popup
          />
        </CardContent>
      </Card>

      {/* New Appointment Dialog */}
      <Dialog open={showNewAppointment} onOpenChange={setShowNewAppointment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Nueva Cita</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAppointment} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del paciente</Label>
              <Input
                value={newAppointment.patient_name}
                onChange={(e) => setNewAppointment({ ...newAppointment, patient_name: e.target.value })}
                required
                data-testid="new-apt-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={newAppointment.patient_phone}
                onChange={(e) => setNewAppointment({ ...newAppointment, patient_phone: e.target.value })}
                required
                data-testid="new-apt-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input
                type="date"
                value={newAppointment.appointment_date}
                onChange={handleDateChange}
                min={format(new Date(), 'yyyy-MM-dd')}
                required
                data-testid="new-apt-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Hora</Label>
              <AvailableTimeSelect
                date={newAppointment.appointment_date}
                value={newAppointment.appointment_time}
                onChange={(value) => setNewAppointment(prev => ({ ...prev, appointment_time: value }))}
                testId="new-apt-time"
              />
            </div>
            <div className="space-y-2">
              <Label>Duración</Label>
              <DurationSelect
                value={newAppointment.duration_minutes}
                onChange={(v) => setNewAppointment({ ...newAppointment, duration_minutes: v })}
                testId="new-apt-duration"
              />
            </div>
            <div className="space-y-2">
              <Label>Motivo de consulta</Label>
              <Textarea
                value={newAppointment.reason_text}
                onChange={(e) => setNewAppointment({ ...newAppointment, reason_text: e.target.value })}
                rows={2}
                data-testid="new-apt-reason"
              />
            </div>
            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea
                value={newAppointment.internal_notes}
                onChange={(e) => setNewAppointment({ ...newAppointment, internal_notes: e.target.value })}
                rows={2}
                data-testid="new-apt-notes"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowNewAppointment(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]" data-testid="create-apt-button">
                Crear Cita
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Appointment Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Detalle de Cita</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <User className="h-5 w-5 text-[#0891B2]" />
                <div>
                  <p className="font-medium">{selectedEvent.patient_name}</p>
                  <p className="text-sm text-slate-500">{selectedEvent.patient_phone}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Clock className="h-5 w-5 text-[#0891B2]" />
                <div>
                  <p className="font-medium">
                    {format(new Date(selectedEvent.appointment_date + 'T12:00:00'), "d 'de' MMMM, yyyy", { locale: es })}
                  </p>
                  <p className="text-sm text-slate-500">{selectedEvent.appointment_time}</p>
                </div>
              </div>

              {selectedEvent.reason_text && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <FileText className="h-5 w-5 text-[#0891B2] mt-0.5" />
                  <div>
                    <p className="font-medium">Motivo</p>
                    <p className="text-sm text-slate-500">{selectedEvent.reason_text}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Estado</Label>
                <Select 
                  value={selectedEvent.status}
                  onValueChange={(value) => handleStatusChange(selectedEvent.id, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agendada">Agendada</SelectItem>
                    <SelectItem value="confirmada">Confirmada</SelectItem>
                    <SelectItem value="en_consulta">En consulta</SelectItem>
                    <SelectItem value="atendida">Atendida</SelectItem>
                    <SelectItem value="no_asistio">No asistió</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setSelectedEvent(null)}
              >
                Cerrar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
